import { Injectable } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import type { ParseResult } from 'papaparse';
import { BaseFileExtension } from '../enums/base-available-file-type.enum';
import { BaseXlsxCsvPreviewComponent } from '../components/base-xlsx-csv-preview/base-xlsx-csv-preview.component';
import { BASE_XLSX_CSV_PREVIEW_MAX_ROWS, BaseXlsxCsvPreview } from '../interfaces/base-xlsx-csv-preview.interface';

/**
 * Reusable "view uploaded spreadsheet" widget.
 * `open(file)` shows the first {@link BASE_XLSX_CSV_PREVIEW_MAX_ROWS} rows of an
 * .xlsx/.csv file in a modal, locally, before any upload. `parse(file)` is the
 * lazy-loaded parsing core (the component calls it on init).
 */
@Injectable({ providedIn: 'root' })
export class BaseXlsxCsvPreviewService {
  constructor(private readonly ngbModal: NgbModal) {}

  open(file: File): NgbModalRef {
    const modalRef = this.ngbModal.open(BaseXlsxCsvPreviewComponent, {
      size: 'lg',
      centered: true,
      scrollable: true,
      modalDialogClass: 'xlsx-csv-preview-dialog',
    });
    modalRef.componentInstance.file = file;

    return modalRef;
  }

  async parse(file: File): Promise<BaseXlsxCsvPreview> {
    const extension = this.getExtension(file);

    if (extension === BaseFileExtension.csv) {
      return this.parseCsv(file);
    }

    if (extension === BaseFileExtension.xlsx) {
      return this.parseXlsx(file);
    }

    throw new Error(`Unsupported file extension: "${extension}"`);
  }

  private async parseCsv(file: File): Promise<BaseXlsxCsvPreview> {
    // Lazy-loaded so PapaParse stays out of the main bundle.
    const papa = await import('papaparse');

    return new Promise<BaseXlsxCsvPreview>((resolve, reject) => {
      papa.parse<string[]>(file, {
        // +2 = header row + the MAX shown data rows + 1 extra row, so `toPreview`
        // can detect (and flag) that the file has more rows than the cap.
        preview: BASE_XLSX_CSV_PREVIEW_MAX_ROWS + 2,
        skipEmptyLines: true,
        complete: (result: ParseResult<string[]>) => resolve(this.toPreview(result.data)),
        error: (error: Error) => reject(error),
      });
    });
  }

  private async parseXlsx(file: File): Promise<BaseXlsxCsvPreview> {
    // Lazy-loaded; read-excel-file parses in a Web Worker so the UI never freezes.
    const { readSheet } = await import('read-excel-file/browser');
    // Sheet 1 = first sheet only (import templates are single-sheet).
    const rows = await readSheet(file, 1);

    return this.toPreview(rows.map((row) => row.map((cell) => this.cellToString(cell))));
  }

  /** First row → headers, rest → data rows capped at the max; flags truncation. */
  private toPreview(allRows: string[][]): BaseXlsxCsvPreview {
    const [header = [], ...dataRows] = allRows;
    const truncated = dataRows.length > BASE_XLSX_CSV_PREVIEW_MAX_ROWS;

    return {
      headers: header,
      rows: dataRows.slice(0, BASE_XLSX_CSV_PREVIEW_MAX_ROWS),
      truncated,
    };
  }

  private cellToString(cell: unknown): string {
    if (cell === null || cell === undefined) {
      return '';
    }

    if (cell instanceof Date) {
      return cell.toLocaleDateString();
    }

    return String(cell);
  }

  private getExtension(file: File): string {
    const parts = file.name.split('.');

    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  }
}
